import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef } from 'react';
import typography from 'storybookConfig/mixins/typography';

export interface SegmentedControlOption {
  label: string;
  value: string;
}

export interface SegmentedControlProps {
  /**
   * Array of options to display
   */
  options: SegmentedControlOption[];
  /**
   * The currently selected value
   */
  value?: string;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;
  /**
   * Whether the control is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * onFocus callback
   */
  onFocus?: () => void;
  /**
   * Whether the control is required
   */
  required?: boolean;
}

const Container = styled.div`
  display: inline-flex;
  border-radius: 24px;
  padding: 4px;
  background-color: ${({ theme }) => theme.color.gray100};
  gap: 4px;
  width: 100%;
`;

const SegmentButton = styled.button<{
  isSelected: boolean;
  disabled?: boolean;
  required?: boolean;
}>`
  border: none;
  border-radius: 20px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  ${typography('Navigation/Nav Link')};

  ${({ theme, isSelected, disabled, required }) => {
    if (disabled) {
      return css`
        cursor: not-allowed;
        color: ${theme.color.bodyTextDisabled};
        background-color: transparent;
      `;
    }

    if (isSelected) {
      return css`
        background-color: ${theme.color.blue500};
        color: ${theme.color.white};
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      `;
    }

    return css`
      background-color: transparent;
      color: ${theme.color.bodyTextPrimary};

      &:hover {
        background-color: ${theme.color.gray200};
      }

      &:focus {
        outline: 2px solid ${theme.color.blue400};
        outline-offset: 2px;
      }

      &:after {
        content: ${required ? "'*'" : 'none'};
        color: ${theme.color.error500};
        margin-left: 4px;
      }
    `;
  }}
`;

/**
 * A segmented control component for selecting one option from a small set of mutually exclusive choices.
 * Provides a button group UI pattern similar to iOS segmented controls.
 */
const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(
  ({ options, value, onChange, disabled, className, name, onFocus, required }, ref) => {
    const handleOptionClick = (optionValue: string) => {
      if (!disabled && onChange) {
        onChange(optionValue);
      }
    };

    return (
      <Container ref={ref} className={className}>
        {options.map((option) => (
          <SegmentButton
            key={option.value}
            type="button"
            isSelected={value === option.value}
            disabled={disabled}
            onClick={() => handleOptionClick(option.value)}
            onFocus={onFocus}
            name={name}
            required={required}
          >
            {option.label}
          </SegmentButton>
        ))}
      </Container>
    );
  }
);

SegmentedControl.displayName = 'SegmentedControl';

export default SegmentedControl;
